import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useState } from 'react';
import { pageScrollDown } from '../SummaryPage';
import {
  OnBoarding,
  PageTitleAndSubTitle,
  WasteCalculatorCard,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { observer } from 'mobx-react';
import { useStores } from '../../stores';
import accessDashboardConstants from '../../core-utils/messages/AccessDashboardConstants';
import { useLocation, useNavigate } from 'react-router-dom';
import { toJS } from 'mobx';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import Snackbar from '../../_components/atoms/Snackbar';
import {
  Typography,
  patientAccessTheme,
} from '@HomaHealth/ui-component-library';
import axios from 'axios';
import properties from '../../core-utils/properties';
import { formatCustomQueriesTypesAndFeaturesResponseData } from '../../core-utils/helper-utils/homePageHelper';
import { CLAIMS_LIST, CONTRACT_ONBOARDING } from '../../core-utils/routes';
import { isCardFeatureAccessible } from '../../core-utils/helper-utils/userAccess';
import Loader from '../../_components/molecules/Loader';

export interface accessDashboardProps {}

const useStyles = makeStyles()({
  root: {
    display: 'flex',
    flexDirection: 'column',
    padding: '40px 0px 66px 63px',
    width: '1220px',
  },
  cards: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '32px',
  },
});

const AccessDashboard: React.FC<accessDashboardProps> = observer(({}) => {
  //constants
  const messages = accessDashboardConstants;
  const { classes } = useStyles();
  const navigate = useNavigate();
  const loginPageStore = useStores().loginPageStore;
  const userAccessRoutes = toJS(loginPageStore.userAccessRoutes);
  const customQueriesStore = useStores().customQueriesStore;
  const employerGroupStore = useStores().employerGroupStore;
  const eg_nid = employerGroupStore.employerGroupResponseData.eg_nid[0];
  const location = useLocation();

  const isContractsPage = location?.pathname?.includes('/compliance-assurance');

  //states
  const [cardsData, setCardsData] = useState<any>([]);
  const [additionalRowData, setAdditionalRowData] = useState<any>([]);
  const [title, setTitle] = useState<string>('');
  const [subTitle, setSubTitle] = useState<string>('');
  const [snackbarState, setSnackbarState] = React.useState<{
    open: boolean;
    message: string;
    severity: MuiColor | undefined;
    duration: null | number;
  }>({
    open: false,
    message: '',
    severity: undefined,
    duration: null,
  });
  const [checkOnBoarding, setCheckOnBoarding] = useState<boolean>(false);
  const [contractsLength, setContractsLength] = useState<number>(0);
  const [planDocsLength, setPlanDocsLength] = useState<number>(0);
  const [mainLoader] = useState<any>(<Loader />);
  const [loadingDocs, setLoadingDocs] = useState<boolean>(false);

  const getTypesAndFeatures = async () => {
    await axios
      .get(
        properties.aiqBaseUrl + properties.features + properties.getAllFeatures,
      )
      .then((res) => {
        customQueriesStore.setTypesAndFeatues(
          formatCustomQueriesTypesAndFeaturesResponseData(res.data.data),
        );
      })
      .catch((error) =>
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000),
      );
  };

  const getCardsData = async () => {
    if (location?.pathname.includes('/dashboard')) {
      setTitle(messages.TITLE);
      setSubTitle(messages.SUB_TITLE);
      setCardsData(messages.DASHBOARD_CARDS);
      setAdditionalRowData(messages.CUSTOM_ANALYSIS_ADDITIONAL_CARDS);
    } else if (location?.pathname?.includes('/compliance-assurance')) {
      setTitle(messages.PLAN_DOCUMENTS_TITLE);
      setSubTitle(messages.PLAN_DOCUMENTS_SUBTITLE);
      setCardsData(messages.PLAN_DOCUMENT_CARDS);
      setAdditionalRowData(messages.PLAYBOOK_CARDS);
      setCheckOnBoarding(true);
    } else if (location?.pathname.includes('/custom-analysis')) {
      getTypesAndFeatures();
      setTitle(messages.CUSTOM_ANALYSIS_TITLE);
      setSubTitle(messages.CUSTOM_ANALYSIS_SUBTITLE);
      setCardsData(messages.CUSTOM_ANALYSIS_CARDS);
      setAdditionalRowData(messages.CUSTOM_ANALYSIS_ADDITIONAL_CARDS);
    } else if (location?.pathname.includes('recommendations')) {
      setTitle(messages.RECOMENDATIONS_TITLE);
      setSubTitle(messages.RECOMMENDATIONS_SUBTITLE);
      setCardsData(messages.RECOMMENDATION_CARDS);
    } else {
      return;
    }
  };

  const getFeaturesAndCategory = (category: string) => {
    switch (category) {
      case 'Members List':
        return {
          category: 'Members',
          features: customQueriesStore.typesAndFeatures?.Members?.features,
        };
      case 'Claims List':
        return {
          category: 'Claims',
          features: customQueriesStore.typesAndFeatures?.Claims?.features,
        };
      default:
        return { category: '', features: [] };
    }
  };

  const handleOnClickCategory = async (
    category: string,
    subGroups: any,
    path: string,
  ) => {
    if (
      toJS(employerGroupStore?.employerGroupResponseData?.eg_nid).length === 0
    ) {
      handleOpenSnackBar('SCREEN NOT ACCESSIBLE', 'error', 8000);
      return;
    }
    if (path === CLAIMS_LIST) {
      const featuresAndCategory = getFeaturesAndCategory(category);
      const startDate = customQueriesStore.currentDates?.startDate;
      const endDate = customQueriesStore.currentDates?.endDate;
      customQueriesStore.setCustomQueriesData(
        '',
        '',
        featuresAndCategory?.category,
        featuresAndCategory?.features,
        [],
        [],
        customQueriesStore.employerGroups,
        {
          start_month: '',
          end_month: '',
          plan_start_month: '',
          start_year: 0,
          end_year: 0,
          plan_end_month: '',
          recent_plan_year: 0,
        },
        {
          startDate,
          endDate,
        },
        '',
        [
          {
            id: 1,
            category: '',
            condition: '',
            property: '',
            dropdownSelectionTypeIndex: 0,
            propertiesPageNumber: 0,
            totalPagesForProperties: 0,
          },
        ],
        '',
      );

      const isCardAccessible = isCardFeatureAccessible(
        path,
        loginPageStore.userResponse.complianceAssuranceScreens ?? [],
        loginPageStore.userResponse.role.name,
      );
      if (!isCardAccessible) {
        handleOpenSnackBar('SCREEN NOT ACCESSIBLE', 'error', 8000);
        return;
      }
      navigate(path, { state: { hideCreateQueryModal: true } });
    } else {
      if (path) {
        const isCardAccessible = isCardFeatureAccessible(
          path,
          [
            ...(loginPageStore.userResponse.complianceAssuranceScreens ?? []),
            ...(loginPageStore.userResponse.riskManagementScreens ?? []),
          ],
          loginPageStore.userResponse.role.name,
        );
        if (!isCardAccessible) {
          handleOpenSnackBar('SCREEN NOT ACCESSIBLE', 'error', 8000);
          return;
        }
        navigate(path);
      } else {
        const isRouteAcessable = subGroups.some(
          (subArray: any) =>
            subArray?.path !== undefined &&
            userAccessRoutes.includes(subArray?.path),
        );
        if (isRouteAcessable) {
          navigate(`/dashboard/${encodeURIComponent(category)}`);
        } else {
          handleOpenSnackBar('SCREEN NOT ACCESSIBLE', 'error', 8000);
        }
      }
    }
  };

  const handleContractOnboarding = () => {
    navigate(CONTRACT_ONBOARDING);
  };

  //Snackbar
  const handleOpenSnackBar = (
    message: string,
    severity: MuiColor,
    duration: number | null,
  ) => {
    setSnackbarState({
      open: true,
      message: message,
      severity: severity,
      duration: duration,
    });
  };

  const handleCloseSnackBar = (
    event?: React.SyntheticEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarState({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
  };
  const getContractsAndPlanDocs = async () => {
    if (location?.pathname?.includes('/compliance-assurance')) {
      try {
        setLoadingDocs(true);
        const [contractsResponse, planDocsResponse] = await Promise.all([
          axios.post(
            properties.aiqBaseUrl +
              properties.contractBaseUrl +
              properties.getContracts,
            { eg_nid },
          ),
          axios.post(
            properties.aiqBaseUrl +
              properties.contractBaseUrl +
              properties.getPlanDocs,
            { eg_nid },
          ),
        ]);

        const contractsLength = contractsResponse?.data?.data?.length || 0;
        const planDocsLength = planDocsResponse?.data?.data?.length || 0;

        setContractsLength(contractsLength);
        setPlanDocsLength(planDocsLength);
      } catch (error) {
        console.error('Failed to get data', error);
      } finally {
        setLoadingDocs(false);
      }
    } else {
      return;
    }
  };

  useEffect(() => {
    getContractsAndPlanDocs();
    getCardsData();
  }, [location.pathname]);

  //components
  //repetitive typography component
  const getTypography = (label: string) => {
    return (
      <Typography
        fontSize="16px"
        fontType="semiBold"
        textColor={patientAccessTheme.palette.text.highemphasis}
        lineHeight="22.4px"
      >
        {label}
      </Typography>
    );
  };

  const Dashboard = React.memo(() => {
    return (
      <Grid
        container
        sx={{
          flexDirection: 'column',
        }}
      >
        {location?.pathname?.includes('/compliance-assurance') && (
          <Grid style={{ marginBottom: '24px' }}>
            {getTypography(messages.PLAN_DOCUMENTS_TITLE1)}
          </Grid>
        )}
        <Grid className={classes.cards}>
          {cardsData?.map((category: any, index: number) => {
            const hasAccess =
              category?.subGroups?.some(
                (sub: any) => sub?.path && userAccessRoutes.includes(sub.path),
              ) ||
              (category?.path && userAccessRoutes.includes(category.path));
            const isNoDocUpload =
              hasAccess &&
              checkOnBoarding &&
              ((category?.category === 'Contracts' && contractsLength === 0) ||
                (category?.category === 'Plan Documents' &&
                  planDocsLength === 0));
            const tabIndexMap: Record<string, number> = {
              Contracts: 0,
              'Plan Documents': 1,
            };
            const tabIndex = tabIndexMap[category?.category ?? ''];
            return (
              <div key={index}>
                <WasteCalculatorCard
                  height="325px"
                  width="358px"
                  title={category?.category}
                  subTitle={category.subTitle}
                  image={category.image}
                  titleFont="16px"
                  showExternalLink={false}
                  isNoAccess={!hasAccess}
                  isNoDocUpload={isNoDocUpload}
                  handleNoAccess={() => {
                    window.location.href = 'mailto:sales@homahealth.us';
                  }}
                  imageHeight="193px"
                  showTooltip={true}
                  handleOnBoarding={() => {
                    if (category?.category === 'Plan Documents') {
                      navigate(CONTRACT_ONBOARDING, { state: { tabIndex } });
                    } else {
                      navigate(CONTRACT_ONBOARDING);
                    }
                  }}
                  handleOnClick={() => {
                    handleOnClickCategory(
                      category?.category,
                      category?.subGroups,
                      category?.path,
                    );
                  }}
                  truncatedSubTitleLength={140}
                />
              </div>
            );
          })}
        </Grid>
      </Grid>
    );
  });

  const AdditionalRow = React.memo(() => {
    return (
      <Grid
        container
        sx={{
          flexDirection: 'column',
        }}
      >
        <Grid style={{ marginBottom: '24px' }}>
          {getTypography(
            location?.pathname?.includes('/compliance-assurance')
              ? messages.PLAYBOOKS_AND_REPORTS
              : messages.CLAIMS_AND_MEMBERS,
          )}
        </Grid>
        <Grid className={classes.cards}>
          {additionalRowData?.map((category: any, index: number) => {
            return (
              <div key={index}>
                <WasteCalculatorCard
                  height="325px"
                  width="358px"
                  title={category?.category}
                  subTitle={category.subTitle}
                  image={category.image}
                  titleFont="16px"
                  showExternalLink={false}
                  imageHeight="193px"
                  showTooltip={true}
                  isNoAccess={
                    !(
                      category?.subGroups?.some(
                        (sub: any) =>
                          sub?.path && userAccessRoutes.includes(sub.path),
                      ) ||
                      (category?.path &&
                        userAccessRoutes.includes(category.path))
                    )
                  }
                  handleNoAccess={() => {
                    window.location.href = 'mailto:sales@homahealth.us';
                  }}
                  handleOnClick={() => {
                    handleOnClickCategory(
                      category?.category,
                      category?.subGroups,
                      category?.path,
                    );
                  }}
                  truncatedSubTitleLength={150}
                />
              </div>
            );
          })}
        </Grid>
      </Grid>
    );
  });

  return (
    <div>
      <Grid container className={classes.root}>
        <Grid style={{ marginBottom: '42px' }}>
          <PageTitleAndSubTitle
            title={title}
            subTitle={subTitle}
            showDownloadButton={false}
            showQueryDataButton={false}
            customButton={isContractsPage}
            customButtonText={'Onboarding'}
            customButtonStartIcon={
              <OnBoarding color={patientAccessTheme.palette.status.white} />
            }
            customButtonVariant={'contained'}
            customButtonClick={handleContractOnboarding}
            customButtonWidth={'161px'}
          />
        </Grid>
        <Grid style={{ marginBottom: '38px' }}>
          {loadingDocs ? mainLoader : <Dashboard />}
        </Grid>
        {additionalRowData.length > 0 && <AdditionalRow />}
        <Snackbar
          {...snackbarState}
          handleClose={handleCloseSnackBar}
          vertical="bottom"
          horizontal="right"
        />
        <div id="scroll-top-element" />
      </Grid>
      {pageScrollDown('scroll-top-element')}
    </div>
  );
});
export default AccessDashboard;
